import { Trans, useLingui } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import {
  BaseActivity,
  BaseActivityArticleName,
  BaseActivityDetailedContent,
  BaseActivityText,
} from '@/components/activities/BaseActivity/BaseActivity';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconRefusal from '@/icons/Refusal.svg';
import { JOB_REFUSED_REASONS } from '@/models/article';

export const JobRefusedActivity = ({
  activity,
  displayArticleName,
}: {
  activity: ActivityOfType<'job_refused'>;
  displayArticleName: boolean;
}) => {
  const { i18n } = useLingui();

  const workshopName = activity.workshop.name;
  const reasonDetail = JOB_REFUSED_REASONS.find(({ id }) => id === activity.data.reason);
  const reason =
    !reasonDetail || reasonDetail.id === 'custom'
      ? undefined
      : i18n._(activity.data.onBehalfOfWorkshop ? reasonDetail.onBehalfLabel : reasonDetail.label);
  const comment = activity.data.comment;

  const creatorName = activity.data.onBehalfOfWorkshop
    ? activity.creator.name
    : `${activity.creator.name} (${workshopName})`;

  return (
    <BaseActivity
      createdAt={activity.createdAt}
      type="warning"
      icon={<IconRefusal />}
      message={
        <BaseActivityText>
          {!activity.data.step ||
            (activity.data.step === 'service-choice' &&
              (activity.data.onBehalfOfWorkshop ? (
                <Trans id="activities.job_refused.message.service_choice.on-behalf">
                  <strong>{creatorName}</strong> refused the job on behalf of {workshopName} for the
                  item{' '}
                  <BaseActivityArticleName
                    activity={activity}
                    displayArticleName={displayArticleName}
                  />{' '}
                  during the diagnosis
                </Trans>
              ) : (
                <Trans id="activities.job_refused.message.service_choice">
                  <strong>{creatorName}</strong> refused the job for the item{' '}
                  <BaseActivityArticleName
                    activity={activity}
                    displayArticleName={displayArticleName}
                  />{' '}
                  during the diagnosis
                </Trans>
              )))}

          {activity.data.step === 'analysis' &&
            (activity.data.onBehalfOfWorkshop ? (
              <Trans id="activities.job_refused.message.analysis.on-behalf">
                <strong>{creatorName}</strong> refused the job on behalf of {workshopName} for the
                item{' '}
                <BaseActivityArticleName
                  activity={activity}
                  displayArticleName={displayArticleName}
                />{' '}
                during the analysis
              </Trans>
            ) : (
              <Trans id="activities.job_refused.message.analysis">
                <strong>{creatorName}</strong> refused the job for the item{' '}
                <BaseActivityArticleName
                  activity={activity}
                  displayArticleName={displayArticleName}
                />{' '}
                during the analysis
              </Trans>
            ))}
          {!!reason && (
            <>
              <Trans id="_general.colon">:</Trans> <InlineText weight="medium">{reason}</InlineText>
            </>
          )}
        </BaseActivityText>
      }
    >
      <Stack style={{ flex: 1, paddingTop: '0.25rem' }} gap="0.5rem">
        {!!comment && <BaseActivityDetailedContent>{comment}</BaseActivityDetailedContent>}
        {activity.data.chargeCancellationFees && (
          <Paragraph size="200">
            <Trans id="activities.job_refused.message.fees">Cancellation fees were applied.</Trans>
          </Paragraph>
        )}
      </Stack>
    </BaseActivity>
  );
};
